import {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {ScheduleFormValidationErrorMessages, ScheduleFormValidator} from '../../model/schedule'
import {selectTheme} from '../../store'
import {
  DefaultTaskScheduleSettingsTextStyle,
  TaskScheduleSettingsDayIntervalInput,
  TaskScheduleSettingsRelativeDayInput,
  TaskScheduleSettingsRemoveExpirationDateButton,
  TaskScheduleSettingsSetExpirationDateButton,
} from './task_schedule_settings_input'
import {useMount} from 'react-use'
import {registerName} from '../../lib/form'
import {isNil} from 'lodash'
import {RIF} from '../../lib'

interface TaskScheduleSettingsRecurringEveryXDaysRelativeForm {
  startDay: number
  endDay?: number
  everyXDays: number
}

export interface TaskScheduleSettingsRecurringEveryXDaysRelativeFormProps {
  values: Partial<TaskScheduleSettingsRecurringEveryXDaysRelativeForm>
  onValidated: (form: TaskScheduleSettingsRecurringEveryXDaysRelativeForm) => void
  onError: () => void
}

export const TaskScheduleSettingsRecurringEveryXDaysRelative = (
  props: TaskScheduleSettingsRecurringEveryXDaysRelativeFormProps,
) => {
  const {values, onValidated, onError} = props

  const {color, fontSize, fontWeight} = selectTheme()

  const textStyle = DefaultTaskScheduleSettingsTextStyle({color, fontSize, fontWeight})

  const defaultEndDay = values.endDay ?? 7
  const [cachedEndDay, setCachedEndDay] = useState<number>(defaultEndDay)

  const {
    register,
    formState: {isValidating, isValid, errors},
    trigger,
    setValue,
    getValues,
  } = useForm<TaskScheduleSettingsRecurringEveryXDaysRelativeForm>({
    mode: 'onChange',
    defaultValues: {
      startDay: values.startDay ?? 1,
      endDay: values.endDay,
      everyXDays: values.everyXDays ?? 3,
    },
  })

  const [showSetExpirationDateButton, setShowSetExpirationDateButton] = useState(isNil(values.endDay))

  const [latestErrorMessage, setLatestErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    for (const error of Object.values(errors)) {
      if (error?.message) {
        setLatestErrorMessage(error.message)
        onError()
        return
      }
    }
    setLatestErrorMessage(null)
  }, [errors.startDay, errors.endDay, errors.everyXDays])

  useMount(() => {
    trigger()
  })

  useEffect(() => {
    if (isValid && !isValidating) {
      const form = getValues()
      onValidated(form)
    }
  }, [isValid, isValidating])

  return (
    <div css={{display: 'flex', flexDirection: 'column', gap: 16}}>
      <div>
        <span css={{...textStyle}}>This task will appear every</span>
        <TaskScheduleSettingsDayIntervalInput
          register={registerName(register, 'everyXDays')}
          failedValidation={!!errors.everyXDays}
          {...{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required('Every X Days'),
            },
            min: {
              value: 1,
              message: ScheduleFormValidationErrorMessages.shouldBePositiveNumber('Every X Days'),
            },
            validate: {
              validEveryXDays: (value) => {
                const [startDay, endDay] = getValues(['startDay', 'endDay'])
                if (!endDay) return true
                const {success} = ScheduleFormValidator.validateEveryXDays({
                  relativeStartDayOrYymmdd: startDay,
                  relativeEndDayOrYymmdd: endDay,
                  scheduleDay: value,
                })
                return success ? true : ScheduleFormValidationErrorMessages.impossibleEveryXDays('Every X Days')
              },
            },
            deps: ['startDay', 'endDay'],
          }}
        />
        <span css={{...textStyle}}>from</span>
        <TaskScheduleSettingsRelativeDayInput
          register={registerName(register, 'startDay')}
          failedValidation={!!errors.startDay || !!errors.endDay}
          {...{
            required: {
              value: true,
              message: ScheduleFormValidationErrorMessages.required('Start Day'),
            },
            min: {
              value: 1,
              message: ScheduleFormValidationErrorMessages.shouldBePositiveNumber('Start Day'),
            },
            validate: {
              lessThanOrEqual: (value) => {
                const endDay = getValues('endDay')
                if (value && endDay && value > endDay) {
                  return ScheduleFormValidationErrorMessages.shouldBeLessThanOrEqual('Start Day', 'End Day')
                }
                return true
              },
            },
            deps: ['endDay'],
          }}
        />
        {RIF(
          showSetExpirationDateButton,
          <>
            <span css={{...textStyle}}>and continue indefinitely.</span>
            <TaskScheduleSettingsSetExpirationDateButton
              onClick={() => {
                setValue('endDay', cachedEndDay)
                setShowSetExpirationDateButton(false)
                trigger()
              }}
            />
          </>,
        )}
        {RIF(
          !showSetExpirationDateButton,
          <>
            <span css={{...textStyle}}>to</span>
            <TaskScheduleSettingsRelativeDayInput
              register={registerName(register, 'endDay')}
              failedValidation={!!errors.endDay || !!errors.startDay}
              {...{
                min: {
                  value: 1,
                  message: ScheduleFormValidationErrorMessages.shouldBePositiveNumber('End Day'),
                },
                validate: {
                  greaterThanOrEqual: (value) => {
                    const startDay = getValues('startDay')
                    if (value && startDay && value < startDay) {
                      return ScheduleFormValidationErrorMessages.shouldBeGreaterThanOrEqual('End Day', 'Start Day')
                    }
                    return true
                  },
                },
                deps: ['startDay'],
              }}
            />
            <TaskScheduleSettingsRemoveExpirationDateButton
              onClick={() => {
                setCachedEndDay(getValues('endDay') ?? defaultEndDay)
                setValue('endDay', undefined)
                setShowSetExpirationDateButton(true)
                trigger(['endDay'])
              }}
            />
          </>,
        )}
      </div>
      {RIF(latestErrorMessage, (message) => (
        <div css={{...textStyle, color: color.warning}}>{message}</div>
      ))}
    </div>
  )
}
